import { orderListQuery } from './../../queries/OrderList/index';
import { all, fork, take, call, select, getContext, takeEvery, put } from 'redux-saga/effects';
import { tracked } from 'shared/utils/sagaWrappers';
import { ACTIVATE, DEACTIVATE } from './actions';
import { SubmissionError } from 'redux-form';
import { ApolloError, ApolloQueryResult, MutationOptions } from 'apollo-client';
import { SagaContext } from 'shared/types/common';
import { Services } from 'init/services';
import { getFormErrors } from 'shared/utils/formatError';
import { actions as sharedActions, LOGIN, HIDE_AUTH_MODAL, actions } from 'shared/actions/app';
import { isAuthenticated as isAuthenticatedSelector } from 'selectors/app';
import { getStockPrice, StockPrice } from 'sagas/getStockPrice';
import { getBalance } from 'sagas/balance';
import { FormAction } from 'shared/types/redux-form';
import { Money } from 'types/common';
import { actions as appActions } from 'actions';
import * as mutation from './mutation.gql';
import * as gtmDataLayer from 'shared/utils/gtmDataLayer';

import {
  StockCreateBuyOrderMutation,
  StockCreateBuyOrderMutationVariables,
} from './__generated__/StockCreateBuyOrderMutation';
import { routine } from './actions';
import { FormData } from './constraints';
import { Props } from './index';
import { mainAccountQuery } from 'shared/queries/MainAccountsQuery/builder';
import { Stocks_StockType } from '../../../__generated__/globalTypes';
import { stockPriceQuery } from 'queries/StockPrice';
import { StockPriceQuery } from 'queries/StockPrice/__generated__/StockPriceQuery';

// @translate
type FormErrors =
  | 'order amount is greater than limit'
  | 'Request price is not within indent of actual price'
  | 'Symbol unknown or not published'
  | 'Cannot receive market data for symbol'
  | 'Invalid BuyOrder trigger'
  | 'Invalid BuyOrder status'
  | 'Please wait until the previous order for this investment is executed';

function* validateSaga(stockId: number, amount: number, ask: Money) {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  const { isAuthenticated } = yield select(isAuthenticatedSelector);

  if (!isAuthenticated) {
    yield put(
      sharedActions.showAuthModal('signin', `stocks/${stockId}`, 'desktopStocksRegistration')
    );
    const action: ReturnType<typeof actions.login> | ReturnType<typeof actions.hideAuthModal> =
      yield take([LOGIN, HIDE_AUTH_MODAL]);
    if (action.type === HIDE_AUTH_MODAL) {
      return false;
    }
  }

  const { data, loading }: ApolloQueryResult<StockPriceQuery> = yield call(
    apolloClient.query,
    stockPriceQuery({ id: stockId })
  );

  if (!data?.stocksRemote?.product) {
    return { loading };
  }

  const { buyFee } = data.stocksRemote.product.fees;

  const margin = 1.05; // reserve in case of price or exchange rate changes
  const balance: number = yield call(getBalance, ask.currency);
  const cost = ask.value * amount;
  const costWithMargin = cost * 1.005 + buyFee.value; // 1.005 - reserver in case of price changes

  if (balance < costWithMargin) {
    yield put(
      sharedActions.showDepositModal(
        {
          value: Math.ceil((costWithMargin - balance) * margin),
          currency: ask.currency,
        },
        `/investments/stocks/${stockId}`
      )
    );
    return false;
  }

  return true;
}

function* stockCreateBuyOrder() {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  yield takeEvery(
    routine.TRIGGER,
    function* ({ payload: { values, props } }: FormAction<FormData, Props>) {
      try {
        const { ask }: StockPrice = yield call(getStockPrice, props.id, 'cache-first');

        if (!ask) {
          return;
        }

        const isValid: boolean = yield validateSaga(props.id, +values.quantity, ask);
        if (!isValid) {
          yield put(routine.success());
          return;
        }

        const options: MutationOptions<
          StockCreateBuyOrderMutation,
          StockCreateBuyOrderMutationVariables
        > = {
          mutation,
          variables: {
            request: {
              quantity: +values.quantity,
              price: {
                value: ask.value,
                currency: ask.currency,
              },
              stockId: props.id,
            },
          },
          refetchQueries: [orderListQuery({ archived: false }), mainAccountQuery()],
          awaitRefetchQueries: true,
        };

        yield call((opt: typeof options) => apolloClient.mutate(opt), options);

        gtmDataLayer.push({
          eventCategory: 'form',
          eventAction: 'stock-orderSubmit',
        });

        yield put(routine.success());

        yield put(appActions.showOrderSubmittedModal(Stocks_StockType.STOCKS));
      } catch (e: ApolloError | any) {
        const errors = getFormErrors(e, (fieldName) =>
          fieldName === 'amount' ? 'amount' : undefined
        );
        yield put(routine.failure(new SubmissionError<FormData, FormErrors>(errors)));
      }
    }
  );
}

export const trackedStockSubmitBuyOrder = tracked(
  function* () {
    yield all([fork(stockCreateBuyOrder)]);
  },
  ACTIVATE,
  DEACTIVATE
);
